/**
 * guardrailsEngine.js — Re Leve IA (Etapa 4)
 * Guardrails determinísticos reais. Sempre vence resposta comercial.
 */

const GUARDRAIL_RULES = {
  G01: {
    id: 'G01',
    label: 'Não prescrever dieta individual',
    intents: ['dieta_prescricao'],
    keywords: ['dieta', 'prescricao', 'plano alimentar', 'quantas calorias devo comer', 'o que devo comer'],
    severity: 'alta',
    stopCommercial: true,
    escalate: false,
    safeResponse: 'Posso te ajudar a registrar consumo e organizar informações, mas recomendações nutricionais individualizadas devem ser feitas por nutricionista. Posso te mostrar como funciona o acompanhamento calórico informativo da Re Leve?',
  },
  G02: {
    id: 'G02',
    label: 'Não prometer emagrecimento',
    intents: ['emagrecimento'],
    keywords: ['emagrecer', 'perder peso', 'emagrecimento', 'secar', 'vou emagrecer', 'perco peso'],
    severity: 'alta',
    stopCommercial: true,
    escalate: false,
    safeResponse: 'As marmitas podem ajudar na organização da rotina alimentar, mas resultados dependem de vários fatores individuais. Posso te ajudar a organizar sua rotina alimentar e registrar seu consumo de forma informativa.',
  },
  G03: {
    id: 'G03',
    label: 'Não assumir restrição sem declaração',
    intents: [],
    keywords: [],
    severity: 'baixa',
    stopCommercial: false,
    escalate: false,
    safeResponse: 'Você possui alguma restrição alimentar, como lactose, glúten ou preferência low carb?',
    complementary: true,
  },
  G04: {
    id: 'G04',
    label: 'Não inventar cardápio do dia',
    intents: ['cardapio'],
    keywords: ['cardapio', 'tem hoje', 'disponivel hoje', 'o que tem hoje', 'menu do dia'],
    severity: 'media',
    stopCommercial: false,
    escalate: false,
    safeResponse: 'Posso te mostrar as principais categorias da Re Leve: combos, proteínas, escondidinhos, sopas, cremes, low carb, sem glúten e sem lactose. Para disponibilidade do dia, confirmo pelo atendimento.',
  },
  G05: {
    id: 'G05',
    label: 'Não expor dados pessoais',
    intents: [],
    keywords: ['telefone de', 'cpf', 'endereco de', 'historico de', 'dados de', 'pagamento de'],
    severity: 'alta',
    stopCommercial: true,
    escalate: true,
    safeResponse: 'Por segurança, não compartilho dados pessoais em conversa aberta.',
  },
  G06: {
    id: 'G06',
    label: 'Consentimento para acompanhamento calórico',
    intents: ['registrar_calorias'],
    keywords: ['caloria', 'calorias', 'registrar consumo', 'acompanhamento calorico'],
    severity: 'media',
    stopCommercial: false,
    escalate: false,
    askConsent: true,
    safeResponse: 'Para registrar seu acompanhamento calórico, preciso da sua autorização para usar essas informações no atendimento. Posso seguir?',
  },
  G08: {
    id: 'G08',
    label: 'Limite de desconto',
    intents: ['desconto_alto'],
    keywords: ['30% de desconto', '40% de desconto', '50% de desconto', 'desconto maior', 'desconto maximo'],
    severity: 'media',
    stopCommercial: true,
    escalate: false,
    safeResponse: 'Posso verificar as condições disponíveis. Para algo fora da política de descontos, encaminho ao atendimento.',
  },
  G09: {
    id: 'G09',
    label: 'Compra coletiva ou corporativa',
    intents: ['compra_coletiva'],
    keywords: ['empresa', 'equipe', 'escritorio', 'funcionarios', 'grupo', 'meu time'],
    severity: 'media',
    stopCommercial: false,
    escalate: true,
    safeResponse: 'Esse pedido pode ser para um volume maior. Você está comprando para você, família ou equipe? Posso ajudar a organizar a melhor opção e encaminhar para atendimento.',
  },
  G10: {
    id: 'G10',
    label: 'Encaminhamento humano',
    intents: ['reclamacao'],
    keywords: ['reclamacao', 'veio errado', 'problema', 'cancelar', 'reembolso', 'reacao alergica', 'alergia grave', 'estragado'],
    severity: 'alta',
    stopCommercial: true,
    escalate: true,
    safeResponse: 'Para te atender com segurança, vou encaminhar essa situação para atendimento humano. Um momento, por favor.',
  },
};

function normalize(text) {
  if (!text) return '';
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

export function analyzeGuardrails({ message, intent, customer }) {
  const norm = normalize(message);
  const triggered = [];

  for (const [id, rule] of Object.entries(GUARDRAIL_RULES)) {
    if (rule.complementary) continue;

    let hit = false;

    // Verificar por intenção
    if (rule.intents.includes(intent)) hit = true;

    // Verificar por keyword (reforço)
    if (!hit && rule.keywords.some(k => norm.includes(normalize(k)))) hit = true;

    // G05: dados de outro cliente — verificar padrões específicos
    if (id === 'G05' && !hit) {
      if (norm.includes('telefone d') || norm.includes('cpf d') || norm.includes('endereco d')) hit = true;
    }

    if (hit) triggered.push(rule);
  }

  // G09 também aciona por segmento do cliente
  if (!triggered.find(r => r.id === 'G09')) {
    const seg = (customer?.segmentoComercial || customer?.segmento || '').toLowerCase();
    if (seg.includes('coletivo')) triggered.push(GUARDRAIL_RULES.G09);
  }

  if (triggered.length === 0) {
    return {
      triggered: false,
      guardrailIds: [],
      severity: 'baixa',
      safeResponse: '',
      shouldStopCommercialRecommendation: false,
      shouldEscalateToHuman: false,
      shouldAskConsent: false,
      explanation: 'Nenhum guardrail acionado.',
      developerNotes: [],
    };
  }

  // Prioridade: alta > media > baixa
  const severityOrder = { alta: 3, media: 2, baixa: 1 };
  triggered.sort((a, b) => (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0));

  const primary = triggered[0];
  const guardrailIds = triggered.map(r => r.id);
  const stopCommercial = triggered.some(r => r.stopCommercial);
  const escalate = triggered.some(r => r.escalate);
  const askConsent = triggered.some(r => r.askConsent);

  const developerNotes = triggered.map(r =>
    `${r.id}: ${r.label} — severidade ${r.severity}${r.stopCommercial ? ' — bloqueia recomendação' : ''}${r.escalate ? ' — requer humano' : ''}`
  );

  return {
    triggered: true,
    guardrailIds,
    severity: primary.severity,
    safeResponse: primary.safeResponse,
    shouldStopCommercialRecommendation: stopCommercial,
    shouldEscalateToHuman: escalate,
    shouldAskConsent: askConsent,
    explanation: `Guardrail(s) acionado(s): ${guardrailIds.join(', ')}`,
    developerNotes,
  };
}

export function getAllGuardrailRules() {
  return Object.values(GUARDRAIL_RULES);
}

export { GUARDRAIL_RULES };
